import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  X,
  Package,
  Truck,
  Calculator,
  ShoppingCart,
  Box,
  MessageCircle,
  TrendingUp,
  Receipt,
  Award,
  Check,
  Loader2,
  Calendar,
  Bell,
  RefreshCw,
  Settings,
  AlertCircle,
  CheckCircle2,
  Info,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/context/AuthContext';

interface HepsiburadaIntegration {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  descriptionEn: string;
  tooltip: string;
  tooltipEn: string;
  icon: typeof Package;
  enabled: boolean;
  status: 'active' | 'inactive' | 'error' | 'requires_activation';
  requiresActivation?: boolean;
  activationNote?: string;
  activationNoteEn?: string;
}

interface HepsiburadaSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  merchantId: string;
  connectionId: string;
  onSave: (settings: HepsiburadaSettings) => void;
}

export interface HepsiburadaSettings {
  syncFrequency: number;
  notifications: boolean;
  integrations: Record<string, boolean>;
}

const defaultIntegrations: HepsiburadaIntegration[] = [
  {
    id: 'listings',
    name: 'Urun Listeleme',
    nameEn: 'Product Listings',
    description: 'Urunlerinizi, stok ve fiyat bilgilerini goruntuluyin',
    descriptionEn: 'View your products, stock and price information',
    tooltip: 'Tum urunlerinizi, stok durumlarini, fiyatlarini ve listeleme durumlarini takip edin. Urun bazli analizler yapin.',
    tooltipEn: 'Track all your products, stock levels, prices and listing status. Perform product-based analysis.',
    icon: Box,
    enabled: true,
    status: 'active',
  },
  {
    id: 'orders',
    name: 'Siparis Entegrasyonu',
    nameEn: 'Order Integration',
    description: 'Siparisleri otomatik olarak cek ve analiz et',
    descriptionEn: 'Automatically fetch and analyze orders',
    tooltip: 'Tum siparis statuslerini takip edin. Siparis trendlerini, ortalama sepet tutarini ve en cok satan urunleri analiz edin.',
    tooltipEn: 'Track all order statuses. Analyze order trends, average basket value and best-selling products.',
    icon: ShoppingCart,
    enabled: true,
    status: 'active',
  },
  {
    id: 'buybox',
    name: 'Buybox Analizi',
    nameEn: 'Buybox Analysis',
    description: 'Urunlerinizin buybox durumunu takip edin',
    descriptionEn: 'Track your products buybox status',
    tooltip: 'Birden fazla saticinin sattigi urunlerde buybox siralamanizi gorun. Buybox fiyatini ve rakiplerinizi analiz edin.',
    tooltipEn: 'See your buybox ranking for products sold by multiple sellers. Analyze buybox price and your competitors.',
    icon: Award,
    enabled: true,
    status: 'active',
  },
  {
    id: 'packages',
    name: 'Kargo Paketleri',
    nameEn: 'Shipping Packages',
    description: 'Kargo paket durumlarini ve teslimat surelerini takip edin',
    descriptionEn: 'Track package status and delivery times',
    tooltip: 'Gonderilen paketlerin durumlarini, kargo firmasi bilgilerini ve teslimat surelerini izleyin.',
    tooltipEn: 'Monitor package status, carrier information and delivery times.',
    icon: Truck,
    enabled: true,
    status: 'active',
  },
  {
    id: 'claims',
    name: 'Iade Talepleri',
    nameEn: 'Return Claims',
    description: 'Iade taleplerini ve sebeplerini analiz et',
    descriptionEn: 'Analyze return requests and reasons',
    tooltip: 'Iade taleplerini, iade sebeplerini ve surec durumlarini takip edin. En cok iade edilen urunleri analiz edin.',
    tooltipEn: 'Track return requests, reasons and process status. Analyze most returned products.',
    icon: Package,
    enabled: true,
    status: 'active',
  },
  {
    id: 'commissions',
    name: 'Komisyon Oranlari',
    nameEn: 'Commission Rates',
    description: 'Urun bazli komisyon oranlarini goruntuluyin',
    descriptionEn: 'View product-based commission rates',
    tooltip: 'Her urun icin gecerli komisyon oranlarini ve odeme vadelerini gorun. Kar marji hesaplamalari yapin.',
    tooltipEn: 'See applicable commission rates and payment terms for each product. Calculate profit margins.',
    icon: Calculator,
    enabled: true,
    status: 'active',
  },
  {
    id: 'transactions',
    name: 'Muhasebe Islemleri',
    nameEn: 'Accounting Transactions',
    description: 'Finansal islemleri ve odemeleri takip edin',
    descriptionEn: 'Track financial transactions and payments',
    tooltip: 'Tum finansal islemlerinizi, hakedis odemelerinizi ve kesintilerinizi detayli olarak goruntuluyin.',
    tooltipEn: 'View all your financial transactions, settlement payments and deductions in detail.',
    icon: Receipt,
    enabled: true,
    status: 'active',
  },
  {
    id: 'performance',
    name: 'Siparis Performansi',
    nameEn: 'Order Performance',
    description: 'Siparis bazli finansal performansi analiz edin',
    descriptionEn: 'Analyze order-based financial performance',
    tooltip: 'Her siparis icin gelir, gider ve kar analizlerini gorun. Performans trendlerini takip edin.',
    tooltipEn: 'View revenue, expense and profit analysis for each order. Track performance trends.',
    icon: TrendingUp,
    enabled: true,
    status: 'active',
  },
  {
    id: 'questions',
    name: 'Saticiya Sor',
    nameEn: 'Customer Q&A',
    description: 'Musteri sorularini takip et ve yanitla',
    descriptionEn: 'Track and respond to customer questions',
    tooltip: 'Musterilerin urunleriniz hakkinda sordugu sorulari goruntuluyin. Bu ozellik icin Merchant Panel\'den yetkilendirme gereklidir.',
    tooltipEn: 'View customer questions about your products. This feature requires authorization from Merchant Panel.',
    icon: MessageCircle,
    enabled: true,
    status: 'active',
    requiresActivation: true,
    activationNote: 'Bu ozellik icin Merchant Panel > Yardim > Satici Destek Talep Formu > API Entegrasyon\'dan yetkilendirme istemeniz gerekebilir.',
    activationNoteEn: 'You may need to request authorization from Merchant Panel > Help > Seller Support Form > API Integration for this feature.',
  },
];

const syncFrequencyOptions = [
  { value: 900, label: '15 Dakika', labelEn: '15 Minutes' },
  { value: 1800, label: '30 Dakika', labelEn: '30 Minutes' },
  { value: 3600, label: 'Saatlik', labelEn: 'Hourly' },
  { value: 21600, label: '6 Saatte Bir', labelEn: 'Every 6 Hours' },
  { value: 43200, label: '12 Saatte Bir', labelEn: 'Every 12 Hours' },
  { value: 86400, label: 'Gunluk', labelEn: 'Daily' },
];

export function HepsiburadaSettingsModal({
  isOpen,
  onClose,
  merchantId,
  connectionId,
  onSave,
}: HepsiburadaSettingsModalProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const { user } = useAuth();

  const [integrations, setIntegrations] = useState<HepsiburadaIntegration[]>(defaultIntegrations);
  const [syncFrequency, setSyncFrequency] = useState(3600);
  const [notifications, setNotifications] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState<'integrations' | 'sync' | 'advanced'>('integrations');

  // Load saved settings on mount
  useEffect(() => {
    if (isOpen && user?.uid && connectionId) {
      loadSettings();
    }
  }, [isOpen, user?.uid, connectionId]);

  const loadSettings = async () => {
    try {
      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
      const response = await fetch(
        `${FUNCTIONS_URL}/getHepsiburadaSettings?userId=${user?.uid}&connectionId=${connectionId}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.settings) {
          setSyncFrequency(data.settings.syncFrequency || 3600);
          setNotifications(data.settings.notifications ?? true);

          if (data.settings.integrations) {
            setIntegrations(prev => prev.map(int => ({
              ...int,
              enabled: data.settings.integrations[int.id] ?? true,
              status: data.settings.integrations[int.id] ? 'active' : 'inactive',
            })));
          }
        }
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const toggleIntegration = (id: string) => {
    setIntegrations(prev =>
      prev.map(integration =>
        integration.id === id
          ? {
              ...integration,
              enabled: !integration.enabled,
              status: !integration.enabled ? 'active' : 'inactive',
            }
          : integration
      )
    );
  };

  const handleSave = async () => {
    if (!user?.uid) return;

    setIsSaving(true);

    const settings: HepsiburadaSettings = {
      syncFrequency,
      notifications,
      integrations: integrations.reduce((acc, int) => {
        acc[int.id] = int.enabled;
        return acc;
      }, {} as Record<string, boolean>),
    };

    try {
      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
      const response = await fetch(`${FUNCTIONS_URL}/saveHepsiburadaSettings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          connectionId,
          settings,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save settings');
      }

      onSave(settings);
      onClose();
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSyncNow = async () => {
    if (!user?.uid) return;

    setIsSyncing(true);

    try {
      // Trigger sync for all enabled integrations
      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';

      const enabledIntegrations = integrations.filter(i => i.enabled);

      // Sync each enabled integration
      for (const integration of enabledIntegrations) {
        const endpoint = getEndpointForIntegration(integration.id);
        if (endpoint) {
          await fetch(`${FUNCTIONS_URL}/${endpoint}?userId=${user.uid}&connectionId=${connectionId}&limit=10`);
        }
      }

      // Small delay for UX
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const getEndpointForIntegration = (id: string): string | null => {
    const endpoints: Record<string, string> = {
      listings: 'getHepsiburadaListings',
      orders: 'getHepsiburadaOrders',
      buybox: 'getHepsiburadaBuybox',
      packages: 'getHepsiburadaPackages',
      claims: 'getHepsiburadaClaims',
      commissions: 'getHepsiburadaCommissions',
      transactions: 'getHepsiburadaTransactions',
      performance: 'getHepsiburadaPerformance',
      questions: 'getHepsiburadaQuestions',
    };
    return endpoints[id] || null;
  };

  const enabledCount = integrations.filter(i => i.enabled).length;

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-border-default flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border-default bg-gradient-to-r from-[#FF6000]/10 to-transparent">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#FF6000] flex items-center justify-center shadow-lg">
              <Settings size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-primary">
                Hepsiburada {isEnglish ? 'Settings' : 'Ayarlari'}
              </h2>
              <p className="text-sm text-text-secondary">
                Merchant ID: {merchantId.substring(0, 8)}... • {enabledCount} {isEnglish ? 'active integrations' : 'aktif entegrasyon'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-bg-elevated transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border-default">
          {[
            { id: 'integrations', label: isEnglish ? 'Integrations' : 'Entegrasyonlar', icon: Box },
            { id: 'sync', label: isEnglish ? 'Sync Settings' : 'Senkronizasyon', icon: RefreshCw },
            { id: 'advanced', label: isEnglish ? 'Advanced' : 'Gelismis', icon: Settings },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={cn(
                'flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-px',
                activeTab === tab.id
                  ? 'border-[#FF6000] text-[#FF6000]'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              )}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === 'integrations' && (
            <div className="space-y-3">
              <p className="text-sm text-text-secondary mb-4">
                {isEnglish
                  ? 'Enable or disable integrations to control what data is synced with Hepsiburada.'
                  : 'Hepsiburada ile senkronize edilecek verileri kontrol etmek icin entegrasyonlari acin veya kapatin.'}
              </p>

              {integrations.map(integration => {
                const Icon = integration.icon;
                return (
                  <div
                    key={integration.id}
                    className={cn(
                      'flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer',
                      integration.enabled
                        ? 'border-[#FF6000]/30 bg-[#FF6000]/5 hover:border-[#FF6000]/50'
                        : 'border-border-default hover:border-border-hover bg-bg-elevated/50'
                    )}
                    onClick={() => toggleIntegration(integration.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={cn(
                          'w-10 h-10 rounded-lg flex items-center justify-center transition-colors',
                          integration.enabled
                            ? 'bg-[#FF6000]/20 text-[#FF6000]'
                            : 'bg-bg-elevated text-text-tertiary'
                        )}
                      >
                        <Icon size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-text-primary">
                            {isEnglish ? integration.nameEn : integration.name}
                          </h4>
                          {integration.requiresActivation && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/20 text-amber-500 border border-amber-500/30">
                              {isEnglish ? 'May need activation' : 'Aktivasyon gerekebilir'}
                            </span>
                          )}
                          <div className="group relative">
                            <Info
                              size={14}
                              className="text-text-tertiary hover:text-[#FF6000] cursor-help transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="absolute left-0 bottom-full mb-2 w-64 p-3 rounded-lg bg-bg-surface border border-border-default shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none">
                              <p className="text-xs text-text-secondary leading-relaxed">
                                {isEnglish ? integration.tooltipEn : integration.tooltip}
                              </p>
                              {integration.requiresActivation && (
                                <p className="text-xs text-amber-500 mt-2 pt-2 border-t border-border-default">
                                  {isEnglish ? integration.activationNoteEn : integration.activationNote}
                                </p>
                              )}
                              <div className="absolute left-3 bottom-0 translate-y-1/2 rotate-45 w-2 h-2 bg-bg-surface border-r border-b border-border-default"></div>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-text-tertiary mt-0.5">
                          {isEnglish ? integration.descriptionEn : integration.description}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {integration.enabled && (
                        <span className="flex items-center gap-1 text-xs text-green-500">
                          <CheckCircle2 size={14} />
                          {isEnglish ? 'Active' : 'Aktif'}
                        </span>
                      )}
                      <div
                        className={cn(
                          'w-11 h-6 rounded-full relative transition-colors',
                          integration.enabled ? 'bg-[#FF6000]' : 'bg-bg-elevated border border-border-default'
                        )}
                      >
                        <div
                          className={cn(
                            'w-5 h-5 rounded-full bg-white absolute top-0.5 transition-transform shadow-sm',
                            integration.enabled ? 'translate-x-5' : 'translate-x-0.5'
                          )}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'sync' && (
            <div className="space-y-6">
              {/* Sync Frequency */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-3">
                  <Calendar size={16} className="text-[#FF6000]" />
                  {isEnglish ? 'Sync Frequency' : 'Senkronizasyon Sikligi'}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {syncFrequencyOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => setSyncFrequency(option.value)}
                      className={cn(
                        'px-3 py-2.5 rounded-lg text-sm font-medium transition-all border',
                        syncFrequency === option.value
                          ? 'border-[#FF6000] bg-[#FF6000]/10 text-[#FF6000]'
                          : 'border-border-default text-text-secondary hover:border-[#FF6000]/50'
                      )}
                    >
                      {isEnglish ? option.labelEn : option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notifications */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-3">
                  <Bell size={16} className="text-[#FF6000]" />
                  {isEnglish ? 'Notifications' : 'Bildirimler'}
                </label>
                <div className="space-y-2">
                  <label
                    className="flex items-center justify-between p-3 rounded-lg border border-border-default cursor-pointer hover:border-[#FF6000]/50 transition-all"
                    onClick={() => setNotifications(!notifications)}
                  >
                    <span className="text-sm text-text-secondary">
                      {isEnglish ? 'Notify me when sync completes' : 'Senkronizasyon tamamlandiginda bildir'}
                    </span>
                    <div
                      className={cn(
                        'w-11 h-6 rounded-full relative cursor-pointer transition-colors',
                        notifications ? 'bg-[#FF6000]' : 'bg-bg-elevated'
                      )}
                    >
                      <div
                        className={cn(
                          'w-5 h-5 rounded-full bg-white absolute top-0.5 transition-transform',
                          notifications ? 'translate-x-5' : 'translate-x-0.5'
                        )}
                      />
                    </div>
                  </label>
                </div>
              </div>

              {/* Manual Sync Button */}
              <div className="pt-4 border-t border-border-default">
                <button
                  onClick={handleSyncNow}
                  disabled={isSyncing}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-[#FF6000] text-[#FF6000] hover:bg-[#FF6000]/10 transition-colors font-medium disabled:opacity-50"
                >
                  {isSyncing ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {isEnglish ? 'Syncing...' : 'Senkronize Ediliyor...'}
                    </>
                  ) : (
                    <>
                      <RefreshCw size={18} />
                      {isEnglish ? 'Sync Now' : 'Simdi Senkronize Et'}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'advanced' && (
            <div className="space-y-4">
              {/* API Info */}
              <div className="p-4 rounded-xl bg-[#FF6000]/5 border border-[#FF6000]/20">
                <div className="flex items-start gap-3">
                  <Info size={20} className="text-[#FF6000] flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-text-primary text-sm">
                      {isEnglish ? 'API Endpoints' : 'API Endpointleri'}
                    </h4>
                    <p className="text-xs text-text-secondary mt-1">
                      {isEnglish
                        ? 'This connection uses the following Hepsiburada API domains:'
                        : 'Bu baglanti asagidaki Hepsiburada API domainlerini kullanir:'}
                    </p>
                    <ul className="text-xs text-text-tertiary mt-2 space-y-1">
                      <li>• listing-external.hepsiburada.com</li>
                      <li>• oms-external.hepsiburada.com</li>
                      <li>• mpfinance-external.hepsiburada.com</li>
                      <li>• api-asktoseller-merchant.hepsiburada.com</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Questions API Note */}
              <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-text-primary text-sm">
                      {isEnglish ? 'Q&A Feature Activation' : 'Soru-Cevap Ozellik Aktivasyonu'}
                    </h4>
                    <p className="text-xs text-text-secondary mt-1">
                      {isEnglish
                        ? 'The "Customer Q&A" feature may require activation from your Merchant Panel. Go to:'
                        : '"Saticiya Sor" ozelligi Merchant Panel\'den aktivasyon gerektirebilir. Su adimlarla erisebilirsiniz:'}
                    </p>
                    <p className="text-xs text-[#FF6000] mt-2 font-mono">
                      Merchant Panel → Yardim → Satici Destek → API Entegrasyon → "Saticiya soru sorulabilsin mi?"
                    </p>
                  </div>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="pt-4 border-t border-border-default">
                <h4 className="text-sm font-medium text-error mb-3">
                  {isEnglish ? 'Danger Zone' : 'Tehlikeli Bolge'}
                </h4>
                <button className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-error text-error hover:bg-error/10 transition-colors font-medium">
                  {isEnglish ? 'Reset All Settings' : 'Tum Ayarlari Sifirla'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-border-default bg-bg-elevated/50">
          <p className="text-xs text-text-tertiary">
            {enabledCount} / {integrations.length} {isEnglish ? 'integrations enabled' : 'entegrasyon aktif'}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
            >
              {isEnglish ? 'Cancel' : 'Iptal'}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium bg-[#FF6000] text-white hover:bg-[#E55500] transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {isEnglish ? 'Saving...' : 'Kaydediliyor...'}
                </>
              ) : (
                <>
                  <Check size={16} />
                  {isEnglish ? 'Save Settings' : 'Ayarlari Kaydet'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
